import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { globalHref } from '@/routing/tenant';
import { translateApiError } from '@/lib/apiError';
import { requestPasswordReset } from '@/features/auth/passwordReset';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/synapse/Field';
import { AuthScreen } from '@/layouts/AuthScreen';
import { BrandPanel } from '@/components/synapse/BrandPanel';

/**
 * "I can't sign in." Collects an address and asks the server to send a link.
 *
 * ── WHY SUCCESS SAYS SO LITTLE ───────────────────────────────────────────────
 *
 * The confirmation is deliberately hedged — "if an account exists for that
 * address" — because the server answers identically whether or not one does, to
 * avoid becoming an unauthenticated staff-directory oracle. The hedge is not
 * padding: it is the ONLY place a user who typo'd their address can learn why
 * nothing arrived. Removing it would leave them waiting on an email that was
 * never going to come, with the interface confidently telling them it was sent.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      // A rate limit is the realistic failure here and it must be visible: the
      // alternative — showing the same success screen — teaches somebody to
      // keep waiting for a mail the server declined to send.
      setError(translateApiError(err, { fallback: t('auth.resetRequestFailed') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthScreen
      brand={
        <BrandPanel
          heading={
            <>
              {t('auth.brand.forgotHeadingLine1')}
              <br />
              {t('auth.brand.forgotHeadingLine2')}
            </>
          }
          body={t('auth.brand.forgotBody')}
          footer={
            <div className="flex w-full justify-between">
              <span>{t('common.appNameLong')}</span>
              <span className="mono">{t('common.version')}</span>
            </div>
          }
        />
      }
    >
      <div className="flex flex-col gap-7">
        <div>
          <h1 className="text-[26px] font-semibold leading-[1.1] tracking-[-0.025em] md:text-[22px]">
            {t('auth.forgotPassword')}
          </h1>
          <p className="mt-2 text-sm leading-[1.45] text-muted md:text-[13px]">
            {sent ? t('auth.resetLinkSentBody') : t('auth.forgotPasswordBody')}
          </p>
        </div>

        {sent ? (
          <div
            className="rounded-lg border border-line bg-paper-2 px-4 py-3 text-[13px] leading-[1.5] text-ink-2"
            role="status"
          >
            {t('auth.resetLinkSentDetail', { email: email.trim() })}
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
            <Field label={t('common.email')}>
              <Input
                type="email"
                autoComplete="email"
                required
                inputSize="lg"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>

            {error && (
              <div className="text-[12.5px] text-destructive" role="alert">
                {error}
              </div>
            )}

            <Button type="submit" size="lg" full disabled={submitting}>
              {submitting ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
            </Button>
          </form>
        )}

        <div className="text-center text-[13px] text-muted">
          {/* A plain anchor, not <Link>: this page is global, but it is reachable
              from a clinic's namespace where <Link to="/login"> would resolve to
              that clinic's prefix. Same reasoning as LoginPage's sign-up link. */}
          <a href={globalHref('/login')} className="text-ink underline underline-offset-[3px]">
            {t('auth.backToSignIn')}
          </a>
        </div>
      </div>
    </AuthScreen>
  );
}
